/**
 * The test-drive date picker.
 *
 * Surface: the Preferred Date field on `/book-a-test-drive/<slug>/`, the only
 * date field on the site.
 *
 * WHY THIS EXISTS AT ALL
 * ----------------------
 * Gravity Forms ships an AccDC / apex4x calendar. Three rounds of CSS and JS
 * overrides each fixed one of its defects and produced another, and the client
 * drove the third round and called it "a nightmare … sluggish as hell - clicks
 * dont do anything or only trigger after a delay". Reproduced with human-paced
 * mouse input (8-step move, 80ms settle, 90ms between press and release): it
 * opened once after 573ms and then never again — not after clicking away and
 * back, not after picking a date. The widget destroys itself on selection and
 * nothing it exposes brings it back.
 *
 * The list of things we were working around, all measured 2026-09-18: it sizes
 * itself in `em` off a root that is 10px here so it rendered at 62.5%; its nav
 * cells carry a bare `btn` class that collides with the chop's `.btn`; it caps
 * its panel at 235px so a grid with 24px-minimum targets paints outside it; it
 * anchors over the field it belongs to; it ships `accesskey="1".."4"`; it sets
 * `aria-expanded` on the input and never resets it; it announces nothing on a
 * month change; every blocked day says the same "Unavailable"; its icon is a
 * `dashicons` span and dashicons is not enqueued on the front end, so a logged
 * out visitor sees no glyph; and it prints "Press F1 for help" under every
 * calendar. Owning ~450 lines of calendar is less code than suppressing all of
 * that, and every one of those defects stops existing rather than being fought.
 *
 * WHAT IS UNCHANGED
 * -----------------
 * The three date rules (`App\Services\TestDrive\TestDriveDateRules`), the
 * per-dealership closed weekdays (`OpeningHoursService::closedWeekdays()`), the
 * server-side `gform_field_validation`, the `dd/mm/yyyy` mask, the stored
 * value, the merge tags and the notifications. This file reads the same
 * `window.bwmDatePicker` payload the old one did and renders a widget instead
 * of configuring one.
 *
 * TWO DESIGN DECISIONS THAT LOOK LIKE BUGS IF YOU DO NOT KNOW THEM
 * ----------------------------------------------------------------
 *   - **The panel is built once and shown/hidden.** It is never constructed per
 *     open and never torn down on selection. That single property is the fix
 *     for the reported complaint; do not "tidy" it into a create/destroy pair.
 *   - **Focus moves into the grid only when the visitor asked for the grid** —
 *     ArrowDown, or the calendar button. An open caused by clicking, tapping or
 *     tabbing into the *text field* leaves the caret in the field. The plan
 *     asked for focus in the grid on every open; on touch that makes typing
 *     impossible, because focus on a button dismisses the soft keyboard, and
 *     the same plan requires typing to stay available at 390px. Pointer opens
 *     therefore keep the caret and the grid stays one Tab away.
 *
 * Contract, rationale and the measured history: documentation/twig-templates.md
 * → "The test-drive date picker".
 * Plan:  documentation/superpowers/plans/2026-09-18-date-picker-replacement.md
 * Guard: tests/playwright/date-picker-usability.spec.ts
 */
(() => {
    'use strict';

    /**
     * The field is found by Gravity Forms' class and then renamed to ours — see
     * `standDownPluginWidget()` for why. Both are listed so a re-run (bfcache,
     * a re-rendered form) still finds it.
     */
    const FIELD = 'input.gform-datepicker, input.bwm-dp-input';
    const PANEL_ID = 'bwm-dp-panel';
    const MONTH_ID = 'bwm-dp-month';

    const MONTHS = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December',
    ];

    const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    /** Column order, Monday first — `getDay()` numbers, so Sunday (0) lands last. */
    const COLUMNS = [1, 2, 3, 4, 5, 6, 0];

    /**
     * Why a day is not bookable. The keys mirror
     * `TestDriveDateRules::REASON_*`; the DOM spells `too-far` with a hyphen
     * because it is an attribute value, not a PHP constant.
     */
    const REASON_SUFFIX = {
        'past': 'that date has passed',
        'closed': 'the dealership is closed that day',
        'too-far': 'that is further ahead than we can book',
    };

    /** Two of the three rules as sentences; `too-far` uses the horizon fallback
        at the call site, which has to interpolate `config.maxMonths`. */
    const REASON_MESSAGE = {
        'past': 'Please choose today or a later date.',
        'closed': 'The dealership is closed that day — please choose another.',
    };

    const pad = (n) => String(n).padStart(2, '0');
    const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const ddmmyyyy = (d) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
    const sameDay = (a, b) => a !== null && b !== null && iso(a) === iso(b);
    const firstOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
    const sameMonth = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

    const startOfToday = () => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    };

    /**
     * Last bookable day. `new Date(y, m + n, d)` overflows a short month the
     * same way PHP's `modify('+n months')` does (31 January + 3 → 1 May), which
     * is why the two agree without a special case.
     */
    const horizonFor = (maxMonths) => {
        const t = startOfToday();
        return new Date(t.getFullYear(), t.getMonth() + maxMonths, t.getDate());
    };

    /**
     * Mirrors `App\Services\TestDrive\TestDriveDateRules::rejectionReason()`.
     * The server is the control; this is the fast feedback in front of it, so
     * the two read the same closed-weekday list and the same month cap, handed
     * over by `book-a-test-drive.php`.
     */
    const rejectionFor = (date, config) => {
        const today = startOfToday();
        const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());

        if (day < today) {
            return 'past';
        }
        if (day > horizonFor(config.maxMonths)) {
            return 'too-far';
        }
        if (config.closedWeekdays.includes(day.getDay())) {
            return 'closed';
        }
        return null;
    };

    /** Parses the dd/mm/yyyy the field's own mask produces. Strict. */
    const parseTyped = (value) => {
        const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(value).trim());
        if (!m) {
            return null;
        }
        const [, dd, mm, yyyy] = m.map(Number);
        const date = new Date(yyyy, mm - 1, dd);
        // Rejects 31/02/2026, which Date would roll forward into March.
        const valid = date.getFullYear() === yyyy && date.getMonth() === mm - 1 && date.getDate() === dd;
        return valid ? date : null;
    };

    const svg = (markup) => {
        const wrap = document.createElement('span');
        wrap.innerHTML = markup;
        return wrap.firstElementChild;
    };

    /**
     * One chevron, drawn once. The other direction is the same path mirrored in
     * CSS (`[data-dir='next'] svg { transform: scaleX(-1) }`), so the two arrows
     * are identical in weight, shape and optical size by construction rather
     * than by matching two glyphs. The widget's `⇐ ⇒ ← →` were literal text
     * characters of four different weights, which is what read as "ugly, too
     * thin".
     */
    const CHEVRON = `<svg viewBox="0 0 20 20" width="20" height="20" fill="none" stroke="currentColor"
        stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M12.5 4 6.5 10l6 6"/></svg>`;

    const CALENDAR_ICON = `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
        stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/></svg>`;

    /**
     * Stands the plugin's widget down.
     *
     * REMOVING THE TOGGLE BUTTON IS NOT ENOUGH, and believing it was cost an
     * afternoon. `assets/js/dist/gform-datepicker.*.min.js` initialises the
     * calendar from a DELEGATED `focusin` on `input.gform-datepicker`, so
     * merely putting the caret in the field still built the whole widget — and
     * its calendar node is a large node INSIDE `.ginput_container_date` that
     * sat over the page and swallowed clicks on everything behind it. It
     * presented as our panel refusing to close when you clicked away, which is
     * the client's original complaint wearing a different hat.
     *
     * Dropping the two classes the plugin's selectors match on is what actually
     * stands it down, and `scripts-theme.min.js` gates the 127KB datepicker
     * chunk on the same `.gform-datepicker` match, so the code mostly never
     * loads either. `bwm-dp-input` replaces them as our own hook — the rule
     * from the chop's gallery bug: if the third party's JS claims a class, give
     * your variant its own name rather than sharing one.
     *
     * The input keeps `data-mask` (a different GF chunk, keyed off the
     * attribute), so `dd/mm/yyyy` still formats as you type, and nothing about
     * the submitted value, the merge tags or the validation changes.
     */
    const standDownPluginWidget = (input) => {
        input.classList.remove('gform-datepicker', 'datepicker');
        input.classList.add('bwm-dp-input');
        document.querySelectorAll('button.gform-datepicker-toggle').forEach((el) => el.remove());
        document.querySelectorAll('kbd[id^="keyboardHint_"]').forEach((el) => el.remove());
        document.querySelectorAll('.gform-datepicker-calendar').forEach((el) => el.remove());
        // It also writes `aria-expanded` onto the bare textbox and never resets
        // it; invalid on an input with no combobox role, and a lie once closed.
        input.removeAttribute('aria-expanded');
    };

    const build = (input, config) => {
        const container = input.closest('.ginput_container_date') ?? input.parentElement;
        const today = startOfToday();

        let view = firstOfMonth(today);
        let chosen = parseTyped(input.value);
        // Set while we hand focus back to the input, so the focus we caused is
        // never read as the visitor asking for the calendar again. Without it,
        // Escape and "pick a day" both reopen the panel they just closed.
        let suppressFocusOpen = false;

        // --- the trigger button, inside the field ---------------------------
        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'bwm-dp-toggle';
        toggle.setAttribute('aria-label', 'Choose date on calendar');
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('aria-controls', PANEL_ID);
        toggle.append(svg(CALENDAR_ICON));
        container.append(toggle);

        // --- the panel, built ONCE ------------------------------------------
        const root = document.createElement('div');
        root.className = 'bwm-dp';
        root.id = PANEL_ID;
        root.setAttribute('role', 'dialog');
        root.setAttribute('aria-modal', 'false');
        root.setAttribute('aria-label', 'Choose a date');
        root.hidden = true;

        const head = document.createElement('div');
        head.className = 'bwm-dp__head';

        const navButton = (dir, label) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'bwm-dp__nav';
            b.dataset.dir = dir;
            b.setAttribute('aria-label', label);
            b.append(svg(CHEVRON));
            return b;
        };

        const prev = navButton('prev', 'Previous month');
        const next = navButton('next', 'Next month');

        const monthHeading = document.createElement('h2');
        monthHeading.className = 'bwm-dp__month';
        monthHeading.id = MONTH_ID;

        head.append(prev, monthHeading, next);

        const table = document.createElement('table');
        table.className = 'bwm-dp__grid';
        table.setAttribute('role', 'grid');
        table.setAttribute('aria-labelledby', MONTH_ID);

        const thead = document.createElement('thead');
        const headRow = document.createElement('tr');
        COLUMNS.forEach((dayNumber) => {
            const th = document.createElement('th');
            th.scope = 'col';
            const abbr = document.createElement('abbr');
            abbr.title = WEEKDAYS[dayNumber];
            abbr.textContent = WEEKDAYS[dayNumber].charAt(0);
            th.append(abbr);
            headRow.append(th);
        });
        thead.append(headRow);

        const tbody = document.createElement('tbody');
        table.append(thead, tbody);
        root.append(head, table);

        const live = document.createElement('div');
        live.className = 'bwm-dp-live';
        live.setAttribute('aria-live', 'polite');
        live.setAttribute('aria-atomic', 'true');

        container.insertAdjacentElement('afterend', root);
        root.insertAdjacentElement('afterend', live);

        // --- rendering -------------------------------------------------------

        const canGo = (delta) => {
            const target = new Date(view.getFullYear(), view.getMonth() + delta, 1);
            return delta < 0
                ? target >= firstOfMonth(today)
                : target <= firstOfMonth(horizonFor(config.maxMonths));
        };

        /**
         * `aria-disabled` and a no-op, never the `disabled` attribute — a
         * disabled control drops out of the tab order, and for the day grid
         * that would make the roving tabindex inconsistent to navigate. Same
         * treatment on the nav so a click at either end of the range does not
         * throw focus back to the document.
         */
        const setNavState = (button, enabled) => {
            button.setAttribute('aria-disabled', enabled ? 'false' : 'true');
        };

        const dayButton = (date) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'bwm-dp__day';
            b.dataset.date = iso(date);
            b.tabIndex = -1;
            b.textContent = String(date.getDate());

            const label = `${WEEKDAYS[date.getDay()]} ${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
            const reason = rejectionFor(date, config);

            if (reason !== null) {
                b.dataset.reason = reason;
                b.setAttribute('aria-disabled', 'true');
                b.setAttribute('aria-label', `${label} — ${REASON_SUFFIX[reason]}`);
            } else {
                b.setAttribute('aria-label', label);
            }

            if (sameDay(date, today)) {
                b.setAttribute('aria-current', 'date');
            }

            // The accent disc means "this is a valid choice", so a date typed
            // by hand that breaks one of the three rules does NOT get it — it
            // would paint an accent disc and a strikethrough on the same cell
            // while the inline error underneath said it was not bookable.
            if (sameDay(date, chosen) && reason === null) {
                b.classList.add('is-chosen');
                b.setAttribute('aria-pressed', 'true');
            }

            return b;
        };

        const render = () => {
            monthHeading.textContent = `${MONTHS[view.getMonth()]} ${view.getFullYear()}`;
            setNavState(prev, canGo(-1));
            setNavState(next, canGo(1));

            tbody.textContent = '';

            const first = firstOfMonth(view);
            // Monday-first offset: getDay() is Sunday-first, so rotate by 6.
            const offset = (first.getDay() + 6) % 7;
            const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
            const rows = Math.ceil((offset + daysInMonth) / 7);

            for (let row = 0; row < rows; row += 1) {
                const tr = document.createElement('tr');
                for (let col = 0; col < 7; col += 1) {
                    const dayNumber = row * 7 + col - offset + 1;
                    const td = document.createElement('td');
                    td.setAttribute('role', 'gridcell');
                    if (dayNumber >= 1 && dayNumber <= daysInMonth) {
                        td.append(dayButton(new Date(view.getFullYear(), view.getMonth(), dayNumber)));
                    }
                    tr.append(td);
                }
                tbody.append(tr);
            }

            setRoving(preferredDay());
        };

        const days = () => [...tbody.querySelectorAll('.bwm-dp__day')];

        const bookable = (button) => button.getAttribute('aria-disabled') !== 'true';

        /** Chosen day if it is on screen, else today, else the first bookable day. */
        const preferredDay = () => {
            const all = days();
            const byDate = (d) => (d === null ? null : all.find((b) => b.dataset.date === iso(d)) ?? null);
            return byDate(chosen) ?? byDate(today) ?? all.find(bookable) ?? all[0] ?? null;
        };

        /** Exactly one day carries `tabindex="0"` at any time. */
        const setRoving = (button) => {
            days().forEach((b) => {
                b.tabIndex = b === button ? 0 : -1;
            });
        };

        const announceMonth = () => {
            live.textContent = `${MONTHS[view.getMonth()]} ${view.getFullYear()}`;
        };

        // --- open / close -----------------------------------------------------

        const isOpen = () => !root.hidden;

        /**
         * Places the panel below the field, and above it only when above has
         * genuinely more room. Comparing the two gaps rather than asking "does
         * it overflow the bottom?" matters: this page smooth-scrolls (Lenis),
         * so a panel opened mid-scroll measured as overflowing and flipped
         * itself 277px ABOVE the top of the viewport, where its days had
         * negative coordinates and could not be clicked at all.
         *
         * Re-run on scroll and resize while open, because the decision goes
         * stale the moment the field moves.
         */
        const position = () => {
            const gap = 8;
            const height = root.offsetHeight;
            const fieldBox = container.getBoundingClientRect();
            const roomBelow = window.innerHeight - fieldBox.bottom - gap;
            const roomAbove = fieldBox.top - gap;
            const above = height > roomBelow && roomAbove > roomBelow;

            // Offsets in px, NOT `top: 100%`. The contract puts the panel AFTER
            // `.ginput_container_date`, so its offset parent is the field's
            // positioned ancestor and `100%` resolves against THAT box — which
            // is the whole `<li>`, label and format hint included, and dropped
            // the calendar ~350px below the field it belongs to. The panel and
            // the container are siblings, so their offsets share one origin.
            root.style.left = `${container.offsetLeft}px`;
            root.style.top = above
                ? `${container.offsetTop - height - gap}px`
                : `${container.offsetTop + container.offsetHeight + gap}px`;

            root.classList.toggle('bwm-dp--above', above);
        };

        const reposition = () => {
            if (isOpen()) {
                position();
            }
        };

        /**
         * Opening is show + render, never construct. `focusGrid` is true only
         * when the visitor asked for the grid (ArrowDown, the calendar button);
         * see the file header for why a pointer open keeps the caret.
         */
        const open = ({ focusGrid = false } = {}) => {
            if (isOpen()) {
                if (focusGrid) {
                    focusPreferred();
                }
                return;
            }
            chosen = parseTyped(input.value);
            view = firstOfMonth(chosen ?? today);
            render();
            root.hidden = false;
            toggle.setAttribute('aria-expanded', 'true');
            position();
            if (focusGrid) {
                focusPreferred();
            }
        };

        const focusPreferred = () => {
            const target = preferredDay();
            if (target !== null) {
                setRoving(target);
                target.focus();
            }
        };

        const close = ({ returnFocus = false } = {}) => {
            if (!isOpen()) {
                return;
            }
            root.hidden = true;
            root.classList.remove('bwm-dp--above');
            toggle.setAttribute('aria-expanded', 'false');
            if (returnFocus) {
                // Focus events dispatch synchronously, so the flag is only ever
                // set across this one call.
                suppressFocusOpen = true;
                input.focus();
                suppressFocusOpen = false;
            }
        };

        const changeMonth = (delta) => {
            if (!canGo(delta)) {
                return;
            }
            view = new Date(view.getFullYear(), view.getMonth() + delta, 1);
            render();
            announceMonth();
        };

        const choose = (date) => {
            chosen = date;
            input.value = ddmmyyyy(date);
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
            close({ returnFocus: true });
        };

        /** Moves the roving focus, pulling the view along when it leaves the month. */
        const moveFocusTo = (date) => {
            if (!sameMonth(date, view)) {
                view = firstOfMonth(date);
                render();
                announceMonth();
            }
            const target = tbody.querySelector(`.bwm-dp__day[data-date="${iso(date)}"]`);
            if (target !== null) {
                setRoving(target);
                target.focus();
            }
        };

        // --- wiring ------------------------------------------------------------

        const inside = (node) => node instanceof Node && (container.contains(node) || root.contains(node));

        // `pointerdown`, not `click`: the measurement the client's complaint is
        // about starts when the mouse goes down, and opening there is what makes
        // it feel instant rather than "after a delay".
        input.addEventListener('pointerdown', () => open());

        toggle.addEventListener('pointerdown', (event) => {
            // Stop the button taking focus from the field on a mouse press; we
            // decide where focus goes below.
            event.preventDefault();
            if (isOpen()) {
                close({ returnFocus: true });
            } else {
                open({ focusGrid: true });
            }
        });

        // A keyboard visitor arriving on the field gets the calendar too, but
        // keeps the caret — they may be about to type.
        input.addEventListener('focus', () => {
            if (!suppressFocusOpen && input.matches(':focus-visible')) {
                open();
            }
        });

        input.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                open({ focusGrid: true });
                return;
            }
            if (event.key === 'Escape' && isOpen()) {
                event.preventDefault();
                close();
            }
        });

        prev.addEventListener('click', () => changeMonth(-1));
        next.addEventListener('click', () => changeMonth(1));

        root.addEventListener('click', (event) => {
            const day = event.target.closest('.bwm-dp__day');
            if (day === null) {
                return;
            }
            // The no-op half of "aria-disabled, never the disabled attribute".
            if (!bookable(day)) {
                return;
            }
            choose(new Date(`${day.dataset.date}T00:00:00`));
        });

        root.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                close({ returnFocus: true });
                return;
            }

            const day = event.target.closest('.bwm-dp__day');
            if (day === null) {
                return;
            }

            const from = new Date(`${day.dataset.date}T00:00:00`);
            const step = (n) => new Date(from.getFullYear(), from.getMonth(), from.getDate() + n);
            const months = (n) => new Date(from.getFullYear(), from.getMonth() + n, from.getDate());

            const moves = {
                ArrowLeft: () => step(-1),
                ArrowRight: () => step(1),
                ArrowUp: () => step(-7),
                ArrowDown: () => step(7),
                // Start and end of the displayed week, per the grid pattern.
                Home: () => step(-((from.getDay() + 6) % 7)),
                End: () => step(6 - ((from.getDay() + 6) % 7)),
                PageUp: () => months(-1),
                PageDown: () => months(1),
            };

            const move = moves[event.key];
            if (move === undefined) {
                return;
            }
            event.preventDefault();
            moveFocusTo(move());
        });

        // Closing on a click outside and on tabbing out are the same intent
        // reached two ways; neither returns focus, because the visitor has
        // already put it somewhere else.
        //
        // CAPTURE phase, both of them: the chop's smooth-scroll library binds
        // its own pointer handlers and the bubbled event does not reliably
        // reach the document, which presented as the panel simply never closing
        // when you clicked the page behind it.
        document.addEventListener('pointerdown', (event) => {
            if (isOpen() && !inside(event.target)) {
                close();
            }
        }, true);

        document.addEventListener('focusin', (event) => {
            if (isOpen() && !inside(event.target)) {
                close();
            }
        }, true);

        window.addEventListener('scroll', reposition, { passive: true });
        window.addEventListener('resize', reposition, { passive: true });

        // A typed date moves the calendar to its month, so reopening does not
        // land the visitor somewhere they have to navigate back from.
        input.addEventListener('change', () => {
            const typed = parseTyped(input.value);
            if (typed !== null) {
                chosen = typed;
                view = firstOfMonth(typed);
                if (isOpen()) {
                    render();
                }
            }
        });

        render();

        return { open, close, isOpen };
    };

    /**
     * The visible format hint and the inline error stay exactly as they were —
     * they belong to the text input, not to the calendar, and the placeholder
     * disappears the moment typing starts (3.3.2).
     */
    const wireTypedValidation = (input, config) => {
        const container = input.closest('.ginput_container_date') ?? input;

        if (document.getElementById(`${input.id}-format`) === null) {
            const hint = document.createElement('span');
            hint.id = `${input.id}-format`;
            hint.className = 'bwm-datepicker-hint';
            hint.textContent = 'Format: dd/mm/yyyy';
            container.insertAdjacentElement('afterend', hint);

            const described = (input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
            described.push(hint.id);
            input.setAttribute('aria-describedby', described.join(' '));
        }

        const showError = (message) => {
            let node = document.getElementById(`${input.id}-bwm-error`);
            if (!message) {
                node?.remove();
                input.removeAttribute('aria-invalid');
                return;
            }
            if (!node) {
                node = document.createElement('span');
                node.id = `${input.id}-bwm-error`;
                node.className = 'bwm-datepicker-error';
                node.setAttribute('role', 'alert');
                container.insertAdjacentElement('afterend', node);
            }
            node.textContent = message;
            input.setAttribute('aria-invalid', 'true');
        };

        const validate = () => {
            const raw = input.value.trim();
            if ('' === raw) {
                showError(null);
                return;
            }
            const parsed = parseTyped(raw);
            if (parsed === null) {
                showError('Please enter the date as dd/mm/yyyy.');
                return;
            }
            const reason = rejectionFor(parsed, config);
            showError(reason === null
                ? null
                : (REASON_MESSAGE[reason] ?? `Please choose a date within the next ${config.maxMonths} months.`));
        };

        input.addEventListener('change', validate);
        input.addEventListener('blur', validate);
    };

    const init = () => {
        const input = document.querySelector(FIELD);
        if (input === null || input.dataset.bwmDp === 'true') {
            return;
        }

        // The server puts the rules on the form wrapper (`data-bwm-date-picker`,
        // JSON) instead of an inline script. Mirrored to `window.bwmDatePicker`
        // so anything that read the old global still can.
        const holder = input.closest('[data-bwm-date-picker]');
        let raw = null;
        try {
            raw = holder === null ? null : JSON.parse(holder.getAttribute('data-bwm-date-picker') || 'null');
        } catch (e) {
            console.error('bwm: date picker rules unreadable', e);
        }
        if (!raw || typeof raw !== 'object') {
            console.error('bwm: date picker rules missing — the date field keeps its plain input');
            return;
        }
        window.bwmDatePicker = raw;
        input.dataset.bwmDp = 'true';

        const config = {
            maxMonths: Number(raw.maxMonths) || 3,
            closedWeekdays: Array.isArray(raw.closedWeekdays) ? raw.closedWeekdays.map(Number) : [],
        };

        standDownPluginWidget(input);
        wireTypedValidation(input, config);
        window.bwmDatePickerInstance = build(input, config);
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
        init();
    }
})();
